<script setup lang="ts">
import type { LatLngTuple } from 'leaflet';
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import 'leaflet.fullscreen';
import { onBeforeUnmount, onMounted, ref, useCssModule, watch } from 'vue';
import * as L from 'leaflet';
import _isEqual from 'lodash/isEqual';

export interface Props {
  stopLocations?: ReadonlyDeep<openapi.JobLocationsContentOpenapi>;
}
const props = withDefaults(defineProps<Props>(), {
  stopLocations: () => [],
});

type StopLocation = NonNullable<Props['stopLocations']>[number];

interface StopPoint {
  latLng: LatLngTuple;
  number: number;
  location: StopLocation;
}

const styles = useCssModule();

const mapEl = ref<HTMLDivElement | null>(null);
let map: L.Map | undefined;
// How the current data should be framed, replayed when the map is revealed.
let applyView: ((target: L.Map) => void) | undefined;
let resizeObserver: ResizeObserver | undefined;
let hasSize = false;

// Bounding box around the whole Australian continent (incl. Tasmania), shown
// while the stop locations are still loading and for jobs that have none.
const australiaBounds = L.latLngBounds([-43.9, 112.5], [-9.8, 154.2]);
// Keeps the stop markers clear of the map edges when fitting the route.
const routePadding: L.PointTuple = [32, 32];
const singleStopZoom = 12;

/**
 * Stops the API could not geocode come back without coordinates - drop those
 * rather than plotting them at [0, 0], off the coast of Africa.
 */
function toStopPoints(stopLocations: Props['stopLocations'] = []): StopPoint[] {
  return stopLocations.flatMap((stop, index) => {
    const latitude = parseFloat(stop.latitude ?? '');
    const longitude = parseFloat(stop.longitude ?? '');
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];

    const stopNumber = Number(stop.stop_number);
    return [
      {
        latLng: [latitude, longitude],
        number: Number.isFinite(stopNumber) ? stopNumber : index + 1,
        location: stop,
      },
    ];
  });
}

/**
 * Built as DOM nodes rather than an HTML string: the address comes from the
 * API, and `textContent` keeps it from being parsed as markup.
 */
function stopPopup(location: StopLocation): HTMLElement | undefined {
  const lines = [
    [location.suburb, location.postcode],
    [location.address_line_2, location.address_line_1],
  ]
    .map((parts) => parts.filter(Boolean).join(', '))
    .filter(Boolean);

  if (!lines.length) return undefined;

  const content = document.createElement('div');
  content.className = styles.stopPopup;

  for (const line of lines) {
    const row = document.createElement('div');
    row.textContent = line;
    content.append(row);
  }

  return content;
}

function stopIcon(stopNumber: number): L.DivIcon {
  return L.divIcon({
    className: styles.stopMarker,
    html: `${stopNumber}`,
    iconSize: [26, 26],
    // Centre the circle on the coordinate instead of hanging it below/right.
    iconAnchor: [13, 13],
  });
}

function drawMap(): void {
  if (!mapEl.value) return;

  map = L.map(mapEl.value, {
    fullscreenControl: true,
    fullscreenControlOptions: {
      position: 'topleft',
    },
  }).setView([-34.460419, 149.67522], 8);

  map.attributionControl.setPrefix(
    `<span style="position: relative; bottom: -1px">
      <?xml version="1.0" encoding="UTF-8"?>
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="20px" height="10px" viewBox="0 0 20 10" version="1.1">
        <g id="surface1">
        <path style=" stroke:none;fill-rule:nonzero;fill:rgb(69.411765%,12.941176%,17.254902%);fill-opacity:1;" d="M 19.960938 0.0976562 L 15.671875 0.0976562 L 10.628906 5.003906 L 15.671875 9.910156 L 19.960938 9.910156 L 14.917969 5.003906 L 19.960938 0.0976562 "/>
        <path style=" stroke:none;fill-rule:nonzero;fill:rgb(9.411765%,13.333333%,25.882353%);fill-opacity:1;" d="M 13.664062 0.0820312 L 9.375 0.0820312 L 3.5 5.578125 L 3.5 0.0820312 L 0.113281 0.0820312 L 0.113281 9.921875 L 3.5 9.921875 L 6.457031 7.058594 L 9.375 9.898438 L 13.664062 9.898438 L 8.621094 4.992188 L 13.664062 0.0820312 "/>
        </g>
      </svg>
    </span>`,
  );

  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  }).addTo(map);

  const stops = toStopPoints(props.stopLocations);

  if (!stops.length) {
    applyView = (target) => target.fitBounds(australiaBounds);
    applyView(map);
    return;
  }

  for (const stop of stops) {
    const popup = stopPopup(stop.location);
    const marker = L.marker(stop.latLng, {
      icon: stopIcon(stop.number),
      // Later stops sit above earlier ones where markers overlap.
      zIndexOffset: stop.number,
      // Nothing to show for a stop with no address - do not offer the click.
      interactive: !!popup,
      keyboard: !!popup,
    }).addTo(map);

    if (popup) marker.bindPopup(popup);
  }

  if (stops.length === 1) {
    const [onlyStop] = stops;
    applyView = (target) => target.setView(onlyStop.latLng, singleStopZoom);
    applyView(map);
    return;
  }

  const polyline = L.polyline(
    stops.map((stop) => stop.latLng),
    {
      color: styles.lineColor,
      weight: 3,
    },
  ).addTo(map);

  applyView = (target) =>
    target.fitBounds(polyline.getBounds(), { padding: routePadding });
  applyView(map);
}

watch(
  () => props.stopLocations,
  (newValues, oldValues) => {
    if (_isEqual(newValues, oldValues)) return;
    if (map?.remove) {
      map.off();
      map.remove();
      map = undefined;
    }
    drawMap();
  },
  // `flush: 'post'` so the redraw runs *after* the parent removes `d-none` from
  // the wrapper — otherwise Leaflet initialises inside a `display: none` (zero
  // size) container and only loads a partial/single tile.
  { flush: 'post' },
);

onMounted(() => {
  // Draw whatever we have straight away. Waiting for the watcher leaves the map
  // blank whenever the stops are already in the store at mount time - the
  // watcher only fires on a *change*.
  drawMap();

  // A parent may still be hiding the container (see the `d-none` wrapper in
  // SmartDispatchJobDetailsSection), in which case Leaflet has just measured
  // itself at zero size. Re-measure and re-frame once it is actually shown.
  resizeObserver = new ResizeObserver(([entry]) => {
    const { width, height } = entry.contentRect;
    const isVisible = width > 0 && height > 0;

    if (isVisible && !hasSize && map) {
      map.invalidateSize();
      applyView?.(map);
    }
    hasSize = isVisible;
  });
  if (mapEl.value) resizeObserver.observe(mapEl.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  map?.off();
  map?.remove();
  map = undefined;
});
</script>

<template>
  <div ref="mapEl" :class="$style.jobMap" data-testid="jobMap"></div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  lineColor: colors.$color-brand-600;
}

.jobMap {
  height: 300px;
}
  // Applied to a Leaflet-built element, so it cannot be nested under .jobMap.
  .stopMarker {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 2px solid colors.$color-white;
    border-radius: 50%;
    background-color: colors.$color-brand-600;
    box-shadow: 0 1px 3px rgba(colors.$color-text-primary, 0.3);
    color: colors.$color-white;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
  }
  .stopPopup {
    color: colors.$color-text-secondary;
    font-size: 13px;
    line-height: 1.4;

    > div:first-child {
      color: colors.$color-text-primary;
      font-weight: 600;
    }
  }
</style>
